import _, { capitalize } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { handleErrors } from '../../../../../utils/functions';
import { showAlert } from '../../../../../redux/slices/appSlice';
import { useAppDispatch } from '../../../../../hooks/reduxHooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFetchs from './useFetchs';

function useUserSave({ drawerAction, handleCloseDrawer, selectedItem }: any) {
    const dispatch = useAppDispatch();
    const [data, setData] = useState<any>();
    const [auxData, setAuxData] = useState<any>(null);
    const [userCuilState, setUserCuilState] = useState<any>('');
    const [loadingUserData, setLoadingUserData] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const scrollRef = useRef<null | HTMLDivElement>(null);
    const {
        roles,
        loadingRoles,
        fetchingRoles,
        allDocumentTypes,
        validatingUserCuil,
        loadingRolePermissions,
        fetchingRolePermissions,
        loadingAllDocumentTypes,
        fetchingAllDocumentTypes,
        addUser,
        updateUser,
        getUserById,
        validateUserCuil,
        getPermissionsByRoleId,
        ...fetchProps
    } = useFetchs({
        drawerAction,
        userCuilState,
        selectedItem
    });

    const getPermissions = useCallback(
        (permissions: any) => {
            return permissions?.map((permission: any) => ({
                id: Number(permission.tipoDocumento.id),
                cargar: permission.cargar,
                descargar: permission.descargar,
                eliminar: permission.eliminar,
                tipoDocumento: allDocumentTypes?.find(
                    (ed: any) => ed.tipoDocumento.id === Number(permission.tipoDocumento.id)
                )?.tipoDocumento
            }));
        },
        [allDocumentTypes]
    );

    const handleChangeTabValue = (event: React.SyntheticEvent | null, newValue: number) => {
        setTabValue(newValue);
        // clearData();
    };

    const formattedRoles = useMemo(() => {
        if (!roles || !allDocumentTypes) return [];
        return roles?.map((role: any) => ({
            ...role,
            permisos: getPermissions(role?.permisos)
        }));
        // eslint-disable-next-line
    }, [roles, allDocumentTypes]);

    const handleChangeData = useCallback((e: any) => {
        const { name, value, type, checked } = e.target;
        setData((prevData: any) => {
            if (!value) {
                const newData = { ...prevData };
                delete newData[name];
                return newData;
            }
            if (name === 'idRol') {
                return {
                    ...prevData,
                    permisos: [],
                    idRol: value
                };
            }
            return {
                ...prevData,
                [name]: type === 'checkbox' ? checked : value
            };
        });
    }, []);

    useEffect(() => {
        if (!data?.cuilUsuario && !!userCuilState) setUserCuilState('');
    }, [data, userCuilState]);

    const clearData = () => {
        setData(null);
        setUserCuilState('');
    };

    useEffect(() => {
        if (allDocumentTypes && drawerAction === 'edit') {
            loadData();
        }
        // eslint-disable-next-line
    }, [selectedItem, drawerAction, allDocumentTypes]);

    const getRolePermissions = async (id: number) => {
        const response: any = await getPermissionsByRoleId(id);
        if (!response?.error) {
            return response?.data?.permisos;
        } else {
            handleErrors(response?.error);
        }
    };

    const loadData = async () => {
        let values: any = null;
        try {
            setLoadingUserData(true);
            const userById: any = await getUserById(selectedItem?.id);
            const rolePermissions = await getRolePermissions(userById?.data?.rol?.id);
            if (!rolePermissions?.error) {
                values = {
                    apellido: userById?.data?.apellido,
                    cuil: userById?.data?.cuil,
                    nombre: userById?.data?.nombre,
                    esAdmin: userById?.data?.esAdmin,
                    id: userById?.data?.id,
                    idRol: { ...userById?.data?.rol, permisos: getPermissions(rolePermissions) },
                    permisos: getPermissions(userById?.data?.permisos)
                };
                setData(values);
                setAuxData(values);
                if (tabValue !== 0) setTabValue(0);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingUserData(false);
        }
    };

    const userCuilValidation = useCallback(async () => {
        if (!!data?.cuilUsuario && data?.cuilUsuario?.length === 11) {
            setUserCuilState({ ...userCuilState, state: '' });
            const response: any = await validateUserCuil(data?.cuilUsuario);

            if (!response?.error) {
                setData({
                    ...data,
                    nombre: response?.data?.nombre,
                    apellido: response?.data?.apellido,
                    esAdmin: false,
                    permisos: []
                });
                setUserCuilState({ cuil: data?.cuilUsuario, state: 'ok' });
            } else {
                setUserCuilState({ cuil: data?.cuilUsuario, state: 'notfound' });
                setData({
                    ...data,
                    idRol: '',
                    nombre: '',
                    apellido: '',
                    esAdmin: false,
                    permisos: []
                });
            }
        }
        // eslint-disable-next-line
    }, [data]);

    useEffect(() => {
        if (!drawerAction) setData(null);
    }, [drawerAction]);

    const handleSubmit = () => {
        if (drawerAction === 'edit') {
            if (
                _.isEqual(
                    { idRol: data?.idRol?.id, esAdmin: data?.esAdmin },
                    { idRol: auxData?.idRol?.id, esAdmin: auxData?.esAdmin }
                )
            ) {
                clearData();
                handleCloseDrawer();
            } else {
                const submit = async () => {
                    const body = {
                        idRol: data?.idRol?.id,
                        esAdmin: data?.esAdmin
                    };
                    const response: any = await updateUser({ userId: auxData?.id, body });
                    if (!response.error) {
                        return enqueueSnackbar(
                            response?.data?.message || 'Datos actualizado con éxito.',
                            {
                                variant: 'success'
                            }
                        );
                    } else {
                        handleErrors(response.error);
                    }
                };
                dispatch(
                    showAlert({
                        title: '¿Actualizar datos de usuario?',
                        keepMounted: true,
                        cancelText: 'Cancelar',
                        confirmText: 'Sí, actualizar',
                        confirmAction: submit
                    })
                );
            }
        } else {
            const submit = async () => {
                const body: any = {
                    ...data,
                    esAdmin: data?.esAdmin || false,
                    idRol: data?.idRol?.id,
                    permisos: data?.permisos?.map((item: any) => ({
                        idTipoDocumento: item?.tipoDocumento?.id,
                        cargar: item?.cargar,
                        descargar: item?.descargar,
                        eliminar: item?.eliminar
                    }))
                };
                const response: any = await addUser(body);
                if (!response?.error) {
                    clearData();
                    handleCloseDrawer();
                    enqueueSnackbar(response?.data?.message || 'Usuario creado con éxito.', {
                        variant: 'success'
                    });
                } else {
                    handleErrors(response?.error);
                }
            };
            dispatch(
                showAlert({
                    title: '¿Crear nuevo usuario?',
                    keepMounted: true,
                    cancelText: 'Cancelar',
                    confirmText: 'Sí, confirmar',
                    confirmAction: submit
                })
            );
        }
    };

    const scrollView = () => {
        setTimeout(() => {
            return scrollRef?.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const getFullName = (selectedItem: any) => {
        const auxFullName = selectedItem?.nombre + ' ' + selectedItem?.apellido;
        return auxFullName
            .split(' ')
            .map((word: string) => capitalize(word))
            .join(' ');
    };

    const copyPermissions = () => {
        if (!!data?.permisos?.length) {
            dispatch(
                showAlert({
                    title: '¿Copiar permisos?',
                    message: 'Se sobreescribirán los permisos actuales.',
                    icon: 'info',
                    keepMounted: true,
                    cancelText: 'Cancelar',
                    confirmText: 'Sí, confirmar',
                    confirmAction: () => {
                        setData({
                            ...data,
                            permisos: data?.idRol?.permisos?.map((item: any) => ({
                                ...item,
                                cargar: item?.cargar,
                                descargar: item?.descargar,
                                eliminar: item?.eliminar
                            }))
                        });
                        scrollView();
                    }
                })
            );
        } else {
            setData({
                ...data,
                permisos: data?.idRol?.permisos
            });
            scrollView();
        }
        // eslint-disable-next-line
    };

    const submitButtonValidation = useMemo(() => {
        return (
            (drawerAction === 'new' && userCuilState?.state !== 'ok') ||
            userCuilState?.cuil !== data?.cuilUsuario ||
            (drawerAction === 'edit' &&
                _.isEqual(
                    { idRol: data?.idRol?.id, esAdmin: data?.esAdmin },
                    { idRol: auxData?.idRol?.id, esAdmin: auxData?.esAdmin }
                ))
        );
    }, [data, userCuilState, auxData, drawerAction]);

    const copyPermissionsButtonValidation = useMemo(() => {
        return _.isEqual(_.sortBy(data?.permisos, ['id']), _.sortBy(data?.idRol?.permisos, ['id']));
    }, [data]);

    const inputCuilValidation = useMemo(() => {
        return !!data?.cuilUsuario && data?.nombre && data?.apellido && !validatingUserCuil;
    }, [data, validatingUserCuil]);

    const loadingRolesNallDocTypes = useMemo(() => {
        return loadingRoles || fetchingRoles || loadingAllDocumentTypes || fetchingAllDocumentTypes;
    }, [loadingRoles, fetchingRoles, loadingAllDocumentTypes, fetchingAllDocumentTypes]);

    const loading = useMemo(() => {
        return (
            drawerAction !== 'new' &&
            (loadingUserData || loadingRolePermissions || fetchingRolePermissions || !data)
        );
    }, [data, loadingUserData, drawerAction, loadingRolePermissions, fetchingRolePermissions]);

    return {
        data,
        roles,
        loading,
        tabValue,
        scrollRef,
        loadingRoles,
        userCuilState,
        fetchingRoles,
        formattedRoles,
        loadingUserData,
        allDocumentTypes,
        validatingUserCuil,
        inputCuilValidation,
        submitButtonValidation,
        loadingAllDocumentTypes,
        fetchingAllDocumentTypes,
        loadingRolesNallDocTypes,
        copyPermissionsButtonValidation,
        clearData,
        scrollView,
        getFullName,
        handleSubmit,
        copyPermissions,
        handleChangeData,
        handleChangeTabValue,
        userCuilValidation,
        ...fetchProps
    };
}

export default useUserSave;
